import { Data, Schema } from "effect";
import { BlockContentHash } from "./ids.ts";

// ─── The two locators (block-locator refactor plan, KTD1) ─────────────────────
//
// `BlockLocator` and `SpanLocator` replace hand-quoted whole-block text as the address of an edit:
// an agent reasons entirely in headings + literal text, and the only opaque token is the short,
// server-minted `contentHash` — always echoed back verbatim from a read, never computed by the
// agent. Both locators are derived from and resolved against the EXISTING `sectionsOf`/
// `resolveSection` heading-path scanner (`outline.ts`/`slice.ts`) — no parser change (KTD8).

// A `BlockLocator` names a whole block by its heading path (curriculum → … → the block itself) plus
// a short content hash. The hash does double duty (KTD2a): it is the compare-and-swap that detects
// drift (a draft rebased mid-edit — see `BlockDrifted` below) so an edit tool can tell the agent to
// re-read, and — because two same-parent, same-title sibling blocks almost always differ in
// content — it disambiguates them without a separate occurrence/index field. Truly identical
// siblings (same heading path AND same content) are interchangeable; resolving to either is correct.
export const BlockLocator = Schema.Struct({
  headingPath: Schema.NonEmptyArray(Schema.String),
  contentHash: BlockContentHash,
});
export type BlockLocator = typeof BlockLocator.Type;

export const blockLocator = (
  headingPath: readonly [string, ...string[]],
  contentHash: BlockContentHash,
): BlockLocator => BlockLocator.make({ headingPath, contentHash });

// A `SpanLocator` names a span by literal `text` unique within one `block` (KTD4) — the single span
// shape shared by surgical edits (`draft-replace`) and diagnostics (annotations, check-suite
// findings, U2). Resolution is scoped strictly to the block's own extent: `text` occurring outside
// `block` is never touched (R-E), and `text` must resolve to exactly one occurrence *within* the
// block — absent or non-unique there fails the same way plain-text locator resolution always has
// (`SpanNotFound`/`SpanAmbiguous`, `locator.ts`).
export const SpanLocator = Schema.Struct({
  block: BlockLocator,
  text: Schema.String,
});
export type SpanLocator = typeof SpanLocator.Type;

export const spanLocator = (block: BlockLocator, text: string): SpanLocator =>
  SpanLocator.make({ block, text });

// A `BlockLocator`'s heading path resolved to a block (so it is not a `SectionNotFound`), but that
// block's CURRENT content hash does not match `contentHash` — the draft/document moved since the
// locator was minted (a rebase, or a concurrent edit; KTD1/KTD6). Carries the block's fresh
// `BlockLocator` (current heading path + current hash) AND `currentText` (the block's full current
// text, exactly what a `read-block` of the same heading path would return) so a retry is one hop with
// no separate re-read: a hash is only ever handed out alongside the full text it covers, never alone
// (the same rule `read-outline`/`search-document` now follow by giving out addresses, not hashes). On
// an edit op the block hash is a hard gate (`draft-edit`/`draft-replace`/`draft-insert` all fail this
// way on a stale hash); on `write-annotation` it instead feeds staleness detection (U2).
export class BlockDrifted extends Data.TaggedError("BlockDrifted")<{
  readonly current: BlockLocator;
  readonly currentText: string;
}> {}
